import API_BASE_URL from '../config';
import { getToken } from './authService';

const CART_KEY = 'cart';
let cartPromise = null;

function readLocal() {
  try {
    const raw = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
    let tradeInMap = {};
    try { tradeInMap = JSON.parse(localStorage.getItem('trade_in_items') || '{}'); } catch (_) {}
    return raw.map(it => {
      const pId = String(it.productId || it.id);
      const isTi = tradeInMap[pId] !== undefined
        ? Boolean(tradeInMap[pId])
        : Boolean(it.tradeIn !== undefined ? it.tradeIn : it.hasTradeIn);
      return {
        ...it,
        tradeIn: isTi,
        hasTradeIn: isTi
      };
    });
  } catch (e) {
    return [];
  }
}

function writeLocal(cart, fireStorage = true) {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
  if (fireStorage) {
    try { window.dispatchEvent(new Event('storage')); } catch (_) {}
  }
  try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: cart } })); } catch (_) {}
}

function mapCartItem(i) {
  const pId = String(i.productId || i.id);
  let isTi = false;
  try {
    const tradeInMap = JSON.parse(localStorage.getItem('trade_in_items') || '{}');
    if (tradeInMap[pId] !== undefined) {
      isTi = Boolean(tradeInMap[pId]);
    } else if (i.tradeIn !== undefined) {
      isTi = Boolean(i.tradeIn);
    } else if (i.hasTradeIn !== undefined) {
      isTi = Boolean(i.hasTradeIn);
    }
  } catch (_) {
    isTi = Boolean(i.tradeIn !== undefined ? i.tradeIn : i.hasTradeIn);
  }

  return {
    id: i.productId || i.id,
    productId: i.productId || i.id,
    orderItemId: i.id,
    name: i.name,
    image: i.image,
    sku: i.sku,
    price: i.price,
    salePrice: i.salePrice !== undefined ? i.salePrice : null,
    offerPrice: i.offerPrice !== undefined ? i.offerPrice : null,
    productPrice: i.productPrice !== undefined ? i.productPrice : null,
    categoryId: i.categoryId || null,
    qty: i.qty || i.quantity || 1,
    services: i.services || [],
    isBundle: i.isBundle || false,
    bundleItems: i.bundleItems || null,
    tradeIn: isTi,
    hasTradeIn: isTi
  };
}

export async function updateItemTradeIn(id, tradeIn) {
  const isTi = Boolean(tradeIn);
  const pId = String(id);

  // 1. Immediately persist to trade_in_items map
  try {
    const tradeInMap = JSON.parse(localStorage.getItem('trade_in_items') || '{}');
    tradeInMap[pId] = isTi;
    localStorage.setItem('trade_in_items', JSON.stringify(tradeInMap));
  } catch (_) {}

  // 2. If guest, update the existing item in local cart without pushing dummy items or wiping other products
  const token = getToken();
  if (!token) {
    try {
      const raw = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
      const idx = raw.findIndex((c) => String(c.productId || c.id) === pId || String(c.id) === pId);
      if (idx >= 0) {
        raw[idx].tradeIn = isTi;
        raw[idx].hasTradeIn = isTi;
        localStorage.setItem(CART_KEY, JSON.stringify(raw));
      }
      return raw;
    } catch (_) {}
  } else {
    // 3. If logged in, sync with server cart
    try {
      const cartFull = await getCartFull();
      const item = (cartFull.items || []).find(
        (i) => String(i.productId) === pId || String(i.id) === pId
      );
      if (item) {
        await fetch(`${API_BASE_URL}/cart/items/${item.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ tradeIn: isTi, hasTradeIn: isTi })
        });
      }
    } catch (err) {
      console.error('Failed to sync tradeIn to server cart:', err);
    }
  }
}

export async function getCart() {
  const token = getToken();
  if (!token) return readLocal();
  if (cartPromise) return cartPromise;
  cartPromise = (async () => {
    const res = await fetch(`${API_BASE_URL}/cart`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return [];
    const data = await res.json();
    const mappedWithServices = data.items.map(mapCartItem);
    try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: mappedWithServices } })); } catch (_) {}
    return mappedWithServices;
  })();
  try {
    return await cartPromise;
  } finally {
    cartPromise = null;
  }
}

export function getCount() {
  const token = getToken();
  if (!token) {
    const cart = readLocal();
    return cart.reduce((s, it) => s + (it.qty || 0), 0);
  }
  // when logged in, client should fetch cart via getCart and derive count; return 0 for sync usage
  return 0;
}

export async function addItem(item, qty = 1) {
  const token = getToken();
  if (!token) {
    const cart = readLocal();
    // consider services/bundle items when deduping local cart entries
    const signature = (it) => {
      if (it.isBundle) {
        const childIds = (it.bundleItems || []).map(c => `${c.id}:${c.side || 'right'}`).sort().join(',');
        return `bundle|${it.id}|${childIds}`;
      }
      return `${String(it.id)}|${(Array.isArray(it.services) ? it.services.map(s => (s && (s.id || s)) || s).sort().join(',') : '')}`;
    };
    const sig = signature(item);
    const idx = cart.findIndex((c) => signature(c) === sig);
    // compute services sum when services provided as objects with price
    let servicesSum = 0;
    let servicesList = [];
    if (Array.isArray(item.services) && item.services.length) {
      if (typeof item.services[0] === 'object') {
        servicesList = item.services.map(s => ({ id: s.id, name: s.name, price: Number(s.price || 0) }));
        servicesSum = servicesList.reduce((s, it) => s + Number(it.price || 0), 0);
      } else {
        servicesList = item.services.slice();
      }
    }
    // per-item stored price calculation (bundle formula vs services)
    let perItemPrice = Number(item.price || 0);
    if (item.isBundle) {
      const leftItems = (item.bundleItems || []).filter(c => c.side === 'left');
      const rightItems = (item.bundleItems || []).filter(c => c.side === 'right' || !c.side);
      const sumLeft = perItemPrice + leftItems.reduce((s, c) => s + Number(c.price || 0), 0);
      const sumRight = rightItems.reduce((s, c) => s + Number(c.price || 0), 0);
      perItemPrice = Math.max(sumLeft, sumRight);
    } else {
      perItemPrice = perItemPrice + Number(servicesSum || 0);
    }
    if (idx >= 0) {
      cart[idx].qty = (cart[idx].qty || 0) + qty;
    } else {
      cart.push({ ...item, qty, services: servicesList, price: perItemPrice });
    }
    writeLocal(cart);
    return cart;
  }
  const res = await fetch(`${API_BASE_URL}/cart/items`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      productId: item.id,
      quantity: qty,
      services: item.services || [],
      isBundle: item.isBundle || false,
      bundleItems: item.bundleItems || []
    })
  });
  if (!res.ok) {
    let errMsg = 'Failed to add item';
    try { const body = await res.json(); if (body && (body.error || body.message)) errMsg = body.error || body.message; } catch (_) {}
    throw new Error(errMsg);
  }
  const data = await res.json();
  const mapped = data.items.map(mapCartItem);
  try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: mapped } })); } catch (_) {}
  // Keep CartPage in sync (it listens to 'storage' even for server carts)
  try { window.dispatchEvent(new Event('storage')); } catch (_) {}
  return mapped;
}

export async function updateQty(id, qty) {
  const token = getToken();
  if (!token) {
    const cart = readLocal();
    const idx = cart.findIndex((c) => String(c.id) === String(id));
    if (idx >= 0) {
      if (qty <= 0) cart.splice(idx, 1); else cart[idx].qty = qty;
      writeLocal(cart);
    }
    return cart;
  }
  // Need to map product id to orderItem id on server; client doesn't know orderItem id. For simplicity, call addItem with negative delta or a special endpoint is required.
  // We'll call server to fetch cart, find item with productId and update by its orderItem id.
  const cart = await getCartFull();
  const item = cart.items.find(i => i.productId === Number(id) || String(i.productId) === String(id));
  if (!item) throw new Error('Item not found in server cart');
  const res = await fetch(`${API_BASE_URL}/cart/items/${item.id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ quantity: qty })
  });
  if (!res.ok) throw new Error('Failed to update item');
  const data = await res.json();
  const mapped = data.items.map(mapCartItem);
  try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: mapped } })); } catch (_) {}
  return mapped;
}

export async function updateItemServices(itemOrProduct, newServices) {
  const token = getToken();
  // determine whether caller passed the full item object or just productId
  let productId = null;
  let originalServices = null;
  if (itemOrProduct && typeof itemOrProduct === 'object') {
    productId = itemOrProduct.id;
    originalServices = Array.isArray(itemOrProduct.services) ? itemOrProduct.services : null;
  } else {
    productId = itemOrProduct;
  }

  if (!token) {
    const cart = readLocal();
    // find index by productId and original services signature when available
    const idx = cart.findIndex((c) => {
      if (String(c.id) !== String(productId)) return false;
      if (!originalServices) return true;
      const a = JSON.stringify(c.services || []);
      const b = JSON.stringify(originalServices || []);
      return a === b;
    });
    if (idx >= 0) {
      const oldServices = Array.isArray(cart[idx].services) ? cart[idx].services : [];
      const oldServicesSum = oldServices.reduce((s, sv) => s + Number((sv && sv.price) || 0), 0);
      const productPrice = Number(cart[idx].price || 0) - oldServicesSum;
      let servicesList = [];
      let servicesSum = 0;
      if (Array.isArray(newServices) && newServices.length) {
        if (typeof newServices[0] === 'object') {
          servicesList = newServices.map(s => ({ id: s.id, name: s.name, price: Number(s.price || 0) }));
          servicesSum = servicesList.reduce((s, it) => s + Number(it.price || 0), 0);
        } else {
          servicesList = newServices.slice();
        }
      }
      cart[idx].services = servicesList;
      cart[idx].price = Number(productPrice) + Number(servicesSum || 0);
      writeLocal(cart);
    }
    try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: cart } })); } catch (_) {}
    return cart;
  }
  const cartFull = await getCartFull();
  // find the matching order item for this product; prefer matching services signature when originalServices available
  const item = cartFull.items.find(i => {
    if (!(i.productId === Number(productId) || String(i.productId) === String(productId))) return false;
    if (!originalServices) return true;
    return JSON.stringify(i.services || []) === JSON.stringify(originalServices || []);
  });
  if (!item) throw new Error('Item not found in server cart');
  const res = await fetch(`${API_BASE_URL}/cart/items/${item.id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ services: newServices })
  });
  if (!res.ok) {
    let errMsg = 'Failed to update item services';
    try { const body = await res.json(); if (body && (body.error || body.message)) errMsg = body.error || body.message; } catch (_) {}
    throw new Error(errMsg);
  }
  const data = await res.json();
  const mapped = data.items.map(mapCartItem);
  try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: mapped } })); } catch (_) {}
  return mapped;
}

export async function removeItem(id) {
  try {
    const tradeInMap = JSON.parse(localStorage.getItem('trade_in_items') || '{}');
    delete tradeInMap[String(id)];
    localStorage.setItem('trade_in_items', JSON.stringify(tradeInMap));
  } catch (_) {}

  const token = getToken();
  if (!token) {
    const cart = readLocal().filter((c) => String(c.id) !== String(id));
    writeLocal(cart);
    return cart;
  }
  const cartFull = await getCartFull();
  const item = cartFull.items.find(i => i.productId === Number(id) || String(i.productId) === String(id));
  if (!item) throw new Error('Item not found');
  const res = await fetch(`${API_BASE_URL}/cart/items/${item.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Failed to remove item');
  const data = await res.json();
  const mapped = data.items.map(mapCartItem);
  try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: mapped } })); } catch (_) {}
  return mapped;
}

export async function clearCart() {
  try {
    sessionStorage.removeItem('glb_old_product');
    sessionStorage.removeItem('glb_from_old_to_new');
    sessionStorage.removeItem('glb_otn_discount');
    sessionStorage.removeItem('glb_otn_category_id');
  } catch (_) {}
  try {
    localStorage.removeItem('trade_in_items');
  } catch (_) {}
  const token = getToken();
  if (!token) { writeLocal([]); return []; }
  const res = await fetch(`${API_BASE_URL}/cart/clear`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Failed to clear cart');
  try { window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: [] } })); } catch (_) {}
  return [];
}

// helper to fetch full cart including orderItem ids
async function getCartFull() {
  const token = getToken();
  if (!token) return { items: [] };
  const res = await fetch(`${API_BASE_URL}/cart`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return { items: [] };
  return await res.json();
}

const cartService = { getCart, getCount, addItem, updateQty, removeItem, clearCart };
export default cartService;
